/**
 * Узнать знакомый стенд по визитке.
 *
 * Заказчик подошёл к KGM второй раз, снял визитку ещё раз — и приложение
 * молча завело второй такой же стенд. Камни разъехались по двойникам, а один
 * из них вовсе не доехал до сервера: обозначение у стендов выводится из
 * названия, и номера камней совпали.
 *
 * Поэтому визитку сверяем с тем, что уже есть. Сверяем осторожно: предложить
 * не тот стенд хуже, чем не предложить ничего, — поэтому совпадением считаем
 * только сильные признаки, а решает всегда человек.
 */
import type { LocalSupplier } from '../db';

/** Правовые формы к делу не относятся: «KGM GEMS CO.,LTD.» и «KGM Gems» — одно и то же. */
const LEGAL = /\b(co|company|ltd|limited|inc|incorporated|corp|corporation|llc|llp|pte|pvt|private|gmbh|sarl|srl|bv|nv|sa|ag|kk|plc)\b/g;

export function nameKey(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[.,()'"«»\-–—/\\&]/g, ' ')
    .replace(LEGAL, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Инициалы пишут и «B.H.C.», и «BHC» — для сравнения склеиваем. */
const squeeze = (v: string) => v.replace(/\s/g, '');

/** Последние девять цифр номера: коды стран и городов пишут по-разному. */
function phoneKey(v: string): string {
  const digits = v.replace(/\D/g, '');
  return digits.length >= 9 ? digits.slice(-9) : '';
}

export interface Twin {
  supplier: LocalSupplier;
  /** Чем именно совпало — это показывается человеку, чтобы он мог не согласиться. */
  reason: string;
}

export function findTwin(
  input: { companyName: string; companyNameLocal?: string; contacts: string[] },
  list: LocalSupplier[],
  exhibitionId: string,
): Twin | null {
  const key = nameKey(input.companyName);
  const localKey = nameKey(input.companyNameLocal ?? '');
  const phones = new Set(input.contacts.map(phoneKey).filter(Boolean));

  // Стенд ищем в пределах той же выставки: одноимённая компания на прошлой
  // выставке — это другой стенд с другим номером и другими камнями.
  const here = list.filter((s) => s.exhibitionId === exhibitionId);

  for (const s of here) {
    const theirs = nameKey(s.companyName);
    const theirsLocal = nameKey(s.companyNameLocal ?? '');
    const same = (a: string, b: string) => Boolean(a) && Boolean(b)
      && (a === b || squeeze(a) === squeeze(b));
    if (same(key, theirs) || same(key, theirsLocal)) {
      return { supplier: s, reason: 'совпало название' };
    }
    if (same(localKey, theirsLocal) || same(localKey, theirs)) {
      return { supplier: s, reason: 'совпало название на визитке' };
    }
  }

  if (phones.size) {
    for (const s of here) {
      for (const c of s.contacts ?? []) {
        if (phoneKey(c.value) && phones.has(phoneKey(c.value))) {
          return { supplier: s, reason: 'совпал телефон' };
        }
      }
    }
  }

  // Одно слово в названии — слишком слабо, чтобы предлагать: «Gems» есть
  // у половины павильона.
  return null;
}
